import { autorun, makeAutoObservable, reaction } from 'mobx';
import { inject, injectable } from 'inversify';
import { fromServerLang, LOCALE, LOCALES, toServerLang } from '@shared/utils/i18n/locales';
import { createIntl, IntlShape } from 'react-intl';
import { messages } from '@shared/utils/i18n';
import i18next from 'i18next';
import { makePersistable } from 'mobx-persist-store';
import moment from 'moment';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
import { saveUserLang } from '@shared/api/userLang';

import 'moment/dist/locale/kk';
import 'moment/dist/locale/ru';

// Режим отображения списка карточек ОСИ в кабинете председателя/админа:
// 'carousel' — текущая карусель (Swiper), 'grid' — адаптивная плитка.
export type OsiViewMode = 'carousel' | 'grid';

@injectable()
export class SettingsStore {
  locale: LOCALE = LOCALES.RU;

  // Выбранный режим отображения карточек ОСИ. По умолчанию — карусель (как было раньше).
  osiViewMode: OsiViewMode = 'carousel';

  intl: IntlShape;

  /**
   * ЯЗЫК ЖИВЁТ НА СЕРВЕРЕ, А В LOCALSTORAGE ЛЕЖИТ ТОЛЬКО ЕГО КЭШ.
   *
   * ═══ ЧТО БЫЛО НЕ ТАК ═══
   * Раньше единственным хранилищем языка был localStorage, и из этого следовали две беды,
   * обе названные заказчиком:
   *   * ПРОТУХАНИЕ. Глобальные настройки персиста (containers/service.ts) ставят expireIn
   *     на сутки и removeOnExpiration, то есть ровно через сутки выбор человека ИСЧЕЗАЛ
   *     и кабинет сам возвращался на русский. Выглядит это как «настройка не сохраняется»;
   *   * ПРИВЯЗКА К БРАУЗЕРУ. С телефона человек оказывался «другим»: там свой localStorage,
   *     и выбранный на компьютере язык туда не попадал никогда.
   *
   * ═══ КАК УСТРОЕНО ТЕПЕРЬ ═══
   * Источник правды — users.lang на сервере, он приезжает полем `lang` ответа /api/Users
   * (то есть тем же запросом, которым кабинет узнаёт текущего пользователя) и применяется
   * реакцией ниже. localStorage остаётся, но в другой роли: это КЭШ ПОСЛЕДНЕГО ИЗВЕСТНОГО
   * значения, и его задача ровно одна — чем-то нарисовать первый кадр, пока ответ сервера
   * не пришёл.
   *
   * ═══ ЧТО ВИДИТ ЧЕЛОВЕК ═══
   *   * ДО ОТВЕТА СЕРВЕРА — язык из кэша, то есть тот же, что в прошлый раз. Ни пустого экрана,
   *     ни «сначала русский, потом казахский»: ответ сервера в подавляющем большинстве случаев
   *     совпадает с кэшем и не меняет ничего;
   *   * ПЕРВЫЙ ВИЗИТ С ЭТОГО БРАУЗЕРА — русский (значение поля по умолчанию), затем, когда
   *     приедет ответ, — выбранный человеком язык. Один переход, и только на первом визите;
   *   * СЕРВЕР НЕДОСТУПЕН — остаётся кэш. Кабинет продолжает работать на прошлом языке,
   *     переключатель тоже работает (локально), ошибок человек не видит;
   *   * ЧЕЛОВЕК ЕЩЁ НЕ ВЫБИРАЛ ЯЗЫК (lang = null) — кэш НЕ перетирается. Именно поэтому
   *     fromServerLang отдаёт null, а не русский: иначе выбор, сделанный до применения
   *     миграции users.lang, молча заменялся бы русским при каждом входе.
   *
   * ═══ ПОЧЕМУ expireIn: 0, А НЕ УДАЛЕНИЕ ПЕРСИСТА ═══
   * Убрать 'locale' из persist целиком значило бы, что первый кадр КАЖДОЙ загрузки страницы
   * рисуется по-русски, а через мгновение переключается, — то есть мигание языком на каждом
   * входе. `expireIn: 0` в опциях СТОРА перебивает глобальные сутки (PersistStore берёт
   * options.expireIn ?? mpsConfig.expireIn, а ноль — это значение, а не отсутствие),
   * и StorageAdapter при нулевом expireIn вообще не пишет отметку истечения. Это ровно то,
   * что нужно кэшу: он обязан переживать сутки, потому что перестал быть источником правды
   */
  // НЕ private ТОЛЬКО ПО ТРЕБОВАНИЮ ТИПОВ MobX: набор аннотаций makeAutoObservable — это
  // AnnotationsMap<this>, а приватные поля в keyof не попадают, и исключить authModule
  // из наблюдения (см. ниже, почему это обязательно) иначе нечем. readonly оставляет
  // ссылку неизменяемой, то есть снаружи это по-прежнему не точка расширения
  constructor(@inject(AuthServiceIdentifier) readonly authModule: AuthService) {
    // authModule ИСКЛЮЧЁН ИЗ НАБЛЮДЕНИЯ ЯВНО, и это не перестраховка. makeAutoObservable
    // применяет к полям observable.deep, а тот ПОДМЕНЯЕТ простые объекты собственными копиями.
    // Сегодня подмены не происходит (AuthServiceImpl — экземпляр класса, а классы deep-энхансер
    // не трогает), то есть всё держится на реализации чужой библиотеки. Случись подмена —
    // стор реагировал бы на КОПИЮ чужого сервиса: серверный язык до кабинета не доезжал бы,
    // а setUserLang писал бы в никуда, причём молча. Своя ссылка на чужой стор наблюдаемой
    // быть и не должна: наблюдаем мы его поля, а не сам факт ссылки
    makeAutoObservable(this, { authModule: false });
    // osiViewMode — настройка браузера (вид списка на конкретном экране), locale — КЭШ
    // серверного значения. Обе живут без срока: протухание кэша языка и было той самой
    // потерей выбора через сутки
    //
    // ═══ ПОДПИСКА НА СЕРВЕРНОЕ ЗНАЧЕНИЕ СТАВИТСЯ ПОСЛЕ ГИДРАТАЦИИ, И ЭТО НЕ ПРИДИРКА ═══
    // makePersistable восстанавливает поля АСИНХРОННО. Поставь мы реакцию сразу, порядок
    // получился бы такой: реакция применила язык с сервера -> гидратация ЗАТЁРЛА его кэшем.
    // Дальше — самое неприятное: когда придёт свежий ответ /api/Users с тем же значением,
    // реакция не сработает вовсе (отслеживаемое выражение не изменилось), и кабинет так
    // и останется на устаревшем кэше до следующей смены языка. Та же гонка гидратации,
    // о которую проект уже спотыкался (см. шапку AuthGuard и when(isHydrated) в OsiServiceImpl).
    // catch нужен, чтобы авария персиста (переполненный/заблокированный localStorage)
    // не оставила кабинет вообще без серверного языка
    void makePersistable(this, {
      name: 'settingsStore',
      properties: ['locale', 'osiViewMode'],
      expireIn: 0,
      removeOnExpiration: false
    })
      .catch(() => undefined)
      .then(() => this.observeServerLang());
    this.intl = createIntl({
      locale: this.locale,
      messages: messages[this.locale]
    });

    autorun(() => {
      this.intl = createIntl({
        locale: this.locale,
        messages: messages[this.locale]
      });
    });

    autorun(() => {
      if (this.locale?.slice(0, 2) === 'kz') {
        i18next.changeLanguage('kk');
        moment().locale('kk');
        return;
      }
      i18next.changeLanguage('ru');
      moment().locale('ru');
    });
  }

  /**
   * СЕРВЕРНОЕ ЗНАЧЕНИЕ СИЛЬНЕЕ КЭША.
   *
   * reaction, А НЕ autorun, И ЭТО НЕСУЩЕЕ РАЗЛИЧИЕ. autorun отслеживает ВСЁ, что прочитал,
   * включая this.locale в сравнении ниже, — и тогда любая локальная смена языка немедленно
   * запускала бы его снова и откатывала выбор к серверному значению: человек нажимал бы
   * «Қазақша» и видел, как кнопка отскакивает обратно. reaction отслеживает ТОЛЬКО выражение
   * в первом аргументе, то есть реагирует на приход данных с сервера и ни на что другое.
   *
   * fireImmediately обязателен: к моменту вызова userData уже может быть восстановлен
   * из персиста или получен ответом /api/Users, и ждать СЛЕДУЮЩЕГО изменения было бы нечего —
   * кабинет остался бы на кэше навсегда.
   *
   * Обратный ход (кабинет -> сервер) живёт в changeLanguage, а НЕ здесь: реагируй мы
   * сохранением на изменение locale, каждый приход серверного значения отправлял бы его же
   * обратно на сервер — и лишним запросом дело бы не кончилось: зеркало в Core на каждую
   * такую запись ходило бы в телеграм
   */
  private observeServerLang = () => {
    reaction(
      () => this.authModule.userData?.lang,
      (serverLang) => {
        const locale = fromServerLang(serverLang);
        if (locale && locale !== this.locale) {
          this.applyLocale(locale);
        }
      },
      { fireImmediately: true }
    );
  };

  get moment() {
    if (i18next.language === 'kk') {
      moment.locale('kk');
    } else {
      moment.locale('ru');
    }
    return moment;
  }

  // Сеттер режима отображения карточек ОСИ — вызывается из переключателя в шапке списка.
  setOsiViewMode = (mode: OsiViewMode) => {
    this.osiViewMode = mode;
  };

  /**
   * ЧЕЛОВЕК ВЫБРАЛ ЯЗЫК В КАБИНЕТЕ.
   *
   * ПОРЯДОК ЗДЕСЬ ПОВЕДЕНЧЕСКИЙ: сначала интерфейс, потом сервер. Переключение обязано быть
   * мгновенным — ждать ответа значило бы показывать человеку задержку на каждое нажатие,
   * а на плохой сети ещё и подозрение, что кнопка не работает.
   *
   * СОХРАНЕНИЕ ФОНОВОЕ И БЕЗ await. Метод остаётся синхронным намеренно: его зовут обработчики
   * onChange у RadioGroup и MUI Select, и сделай мы его async — вызывающие либо получили бы
   * необработанный промис, либо начали бы его ждать, что вернуло бы задержку.
   *
   * ОШИБКУ СОХРАНЕНИЯ ЧЕЛОВЕК НЕ ВИДИТ, и это сознательно: сервер может быть недоступен,
   * сессии может не быть вовсе (переключатель стоит и в шапке лендинга), а язык при этом
   * обязан переключиться. Цена названа честно: неудачное сохранение заметно только тем,
   * что после следующего входа вернётся прежний язык — то есть ровно то же, что было
   * до этого этапа, и не хуже
   */
  changeLanguage = (language: LOCALE) => {
    this.applyLocale(language);

    // catch здесь — последний рубеж, а не дубль. saveUserLang по контракту не бросает никогда
    // (см. его докблок), но промис запускается БЕЗ await: сломай кто-нибудь этот контракт —
    // отказ стал бы необработанным отклонением промиса, то есть ошибкой в консоли у человека,
    // всего лишь переключившего язык, и (в браузерах с window.onunhandledrejection) поводом
    // для ложного отчёта об ошибке
    void this.persistLanguage(language).catch(() => undefined);
  };

  /**
   * Применить язык к интерфейсу. Императивные вызовы i18next/moment здесь ДУБЛИРУЮТ autorun
   * выше не по недосмотру: autorun отработает лишь на следующем тике реактивности, а словари
   * i18next грузятся асинхронно, и полтика задержки успевают проявиться подмигиванием
   * подписей. Так это было сделано и до появления серверного языка — поведение сохранено
   */
  private applyLocale = (language: LOCALE) => {
    this.locale = language;

    if (language.slice(0, 2) === 'kz') {
      i18next.changeLanguage('kk');
      moment().locale('kk');
      return;
    }

    i18next.changeLanguage('ru');
    moment().locale('ru');
  };

  /**
   * Отправить выбор на сервер и, если он принят, запомнить его в данных пользователя.
   *
   * ЗАПОМИНАТЬ ОБЯЗАТЕЛЬНО: userData персистится, а реакция выше адаптирует именно userData.lang.
   * Оставь мы там прежнее значение — при следующей загрузке страницы реакция сработала бы
   * со СТАРЫМ языком и откатила бы кэш, а затем пришёл бы ответ сервера с новым. Человек
   * увидел бы мигание ровно там, где его быть не должно
   */
  private persistLanguage = async (language: LOCALE) => {
    const lang = toServerLang(language);
    const saved = await saveUserLang(this.authModule.id ?? this.authModule.userData?.id, lang);

    if (saved) {
      this.authModule.setUserLang(lang);
    }
  };
}
